import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle, WarningCircle, Spinner, ChartLineUp, Scales, TrendUp,
  ShieldCheck, Sparkle, Download, ArrowsClockwise,
} from "@phosphor-icons/react";

export default function FinancialHealthDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/financial-health/${id}`);
      setItem(data);
      setLoadError(null);
    } catch (e) {
      setLoadError(e.response?.data?.detail || "Unable to load this analysis.");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (item?.status !== "analysing") return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [item?.status, load]);

  const generateSummary = async () => {
    setGenerating(true);
    try {
      await api.post(`/financial-health/${id}/exec-summary`);
      toast.success("Executive summary generated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const downloadPdf = async () => {
    const token = localStorage.getItem("orixa_token") || "";
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/financial-health/${id}/exec-summary/pdf`;
    try {
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("PDF unavailable");
      const blob = await resp.blob();
      const dl = document.createElement("a");
      dl.href = URL.createObjectURL(blob);
      dl.download = `exec-summary-${id}.pdf`;
      dl.click();
    } catch { toast.error("Failed to download PDF"); }
  };

  if (loadError) {
    return (
      <div className="p-8 lg:p-12 max-w-2xl" data-testid="fh-detail-error" role="alert">
        <Link to="/financial-health" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft size={12} weight="bold" /> All analyses
        </Link>
        <div className="mt-6 inline-flex items-center gap-2 text-overline text-rose-600">
          <WarningCircle size={14} weight="bold" /> Analysis unavailable
        </div>
        <h1 className="font-display text-3xl tracking-tight mt-2">{loadError}</h1>
        <button onClick={load} className="mt-5 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2.5 text-sm font-semibold">
          <ArrowsClockwise size={14} weight="bold" /> Retry
        </button>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="p-8" data-testid="fh-detail-loading" role="status">
        <Spinner size={20} weight="bold" className="animate-spin text-primary" />
      </div>
    );
  }

  const a = item.analysis;

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-5xl" data-testid="fh-detail-page">
      <Link to="/financial-health" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
        <ArrowLeft size={12} weight="bold" /> All analyses
      </Link>
      <div className="mt-4 text-overline text-primary">Financial Health</div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-2">{item.label || item.filename}</h1>
      <div className="text-xs text-muted-foreground font-mono mt-2">
        {(item.created_at || "").slice(0, 16).replace("T", " ")} · {item.filename}
      </div>

      {item.status === "analysing" && (
        <div className="mt-8 bg-card border border-border rounded-2xl p-6 inline-flex items-center gap-3">
          <Spinner size={16} weight="bold" className="animate-spin text-primary" />
          <div>
            <div className="font-display text-lg tracking-tight">AI analysis in progress</div>
            <div className="text-xs text-muted-foreground">This typically takes 30-60 seconds.</div>
          </div>
        </div>
      )}
      {item.status === "failed" && (
        <div className="mt-8 bg-card border border-border rounded-2xl p-6 max-w-xl" role="alert">
          <div className="inline-flex items-center gap-2 text-overline text-rose-600">
            <WarningCircle size={14} weight="bold" /> Analysis failed
          </div>
          <div className="mt-2 text-sm">{item.error || "The AI could not process this file."}</div>
        </div>
      )}

      {a && item.status === "ready" && (
        <>
          <div className="mt-8 bg-primary text-primary-foreground rounded-2xl p-6 sm:p-8 card-elev">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80 mb-2">Headline</div>
            <div className="font-display text-xl sm:text-2xl tracking-tight leading-snug">{a.headline}</div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section title="Strengths" icon={CheckCircle} colorClass="text-emerald-600 bg-emerald-50" items={a.financial_strengths} testid="fh-strengths" />
            <Section title="Weaknesses" icon={WarningCircle} colorClass="text-rose-600 bg-rose-50" items={a.financial_weaknesses} testid="fh-weaknesses" />
          </div>

          {a.key_ratios?.length > 0 && (
            <div className="mt-6 bg-card border border-border rounded-2xl p-5 sm:p-6" data-testid="fh-ratios">
              <div className="inline-flex items-center gap-2 text-overline text-muted-foreground mb-4">
                <Scales size={12} weight="bold" /> Key ratios
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {a.key_ratios.map((r, i) => (
                  <div key={`ratio-${i}`} className="border border-border rounded-lg p-3">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{r.name}</div>
                    <div className="font-mono text-2xl mt-1">{r.value}</div>
                    {r.interpretation && <div className="text-xs text-muted-foreground mt-1">{r.interpretation}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Observation title="Liquidity" icon={ChartLineUp} text={a.liquidity} />
            <Observation title="Profitability" icon={TrendUp} text={a.profitability} />
            <Observation title="Solvency" icon={ShieldCheck} text={a.solvency} />
          </div>

          {a.priority_recommendations?.length > 0 && (
            <div className="mt-6 bg-card border border-border rounded-2xl p-5 sm:p-6" data-testid="fh-recommendations">
              <div className="inline-flex items-center gap-2 text-overline text-gold mb-4">
                <Sparkle size={12} weight="bold" /> Priority recommendations
              </div>
              <ol className="space-y-3">
                {a.priority_recommendations.map((r, i) => (
                  <li key={`rec-${i}`} className="flex gap-3">
                    <div className="h-6 w-6 rounded-full bg-gold-soft text-gold grid place-items-center text-sm font-bold shrink-0">{i + 1}</div>
                    <div className="text-sm leading-relaxed">{r}</div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Executive summary */}
          <div className="mt-8 bg-card border border-border rounded-2xl p-6 sm:p-8" data-testid="fh-exec-summary-section">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-overline text-muted-foreground">Stakeholder-ready</div>
                <div className="font-display text-2xl tracking-tight mt-1">AI Executive Summary</div>
                <div className="text-sm text-muted-foreground mt-1 max-w-md">
                  Generate a one-page memo suitable for banks, investors and business partners.
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={generateSummary} disabled={generating}
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50"
                  data-testid="fh-generate-summary">
                  {generating ? <Spinner size={14} weight="bold" className="animate-spin" /> : <Sparkle size={14} weight="bold" />}
                  {item.exec_summary ? "Regenerate" : "Generate"}
                </button>
                {item.exec_summary && (
                  <button onClick={downloadPdf}
                    className="inline-flex items-center gap-2 border border-border bg-card rounded-lg px-4 py-2 text-sm font-semibold hover:bg-secondary"
                    data-testid="fh-download-pdf">
                    <Download size={14} weight="bold" /> PDF
                  </button>
                )}
              </div>
            </div>

            {item.exec_summary && (
              <div className="mt-6 space-y-4 border-t border-border pt-6" data-testid="fh-exec-summary-content">
                <h3 className="font-display text-xl tracking-tight">{item.exec_summary.title}</h3>
                {item.exec_summary.opening_paragraph && (
                  <p className="text-sm leading-relaxed">{item.exec_summary.opening_paragraph}</p>
                )}
                {item.exec_summary.highlights?.length > 0 && (
                  <div>
                    <div className="text-overline text-muted-foreground mb-2">Highlights</div>
                    <ul className="text-sm space-y-1.5 list-disc list-inside">
                      {item.exec_summary.highlights.map((h, i) => <li key={`h-${i}`}>{h}</li>)}
                    </ul>
                  </div>
                )}
                {item.exec_summary.risks_and_watchouts?.length > 0 && (
                  <div>
                    <div className="text-overline text-muted-foreground mb-2">Risks &amp; watch-outs</div>
                    <ul className="text-sm space-y-1.5 list-disc list-inside">
                      {item.exec_summary.risks_and_watchouts.map((r, i) => <li key={`r-${i}`}>{r}</li>)}
                    </ul>
                  </div>
                )}
                {item.exec_summary.call_to_action && (
                  <div className="text-sm font-semibold text-primary">{item.exec_summary.call_to_action}</div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, colorClass, items, testid }) {
  if (!items?.length) return null;
  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid={testid}>
      <div className={`inline-flex items-center gap-2 text-overline ${colorClass} rounded-full px-2.5 py-1 mb-3`}>
        <Icon size={12} weight="bold" /> {title}
      </div>
      <ul className="text-sm space-y-2">
        {items.map((s, i) => <li key={`${title}-${i}`} className="flex gap-2"><span className="text-muted-foreground">•</span><span>{s}</span></li>)}
      </ul>
    </div>
  );
}

function Observation({ title, icon: Icon, text }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="inline-flex items-center gap-2 text-overline text-muted-foreground mb-2">
        <Icon size={12} weight="bold" /> {title}
      </div>
      <div className="text-sm leading-relaxed">{text || "—"}</div>
    </div>
  );
}
